/**
 * This file is a modified version of the open source gcs-browser-upload library
 * https://github.com/QubitProducts/gcs-browser-upload/blob/master/src/Upload.js
 *
 * The original library is licensed under the MIT License, Copyright (c) 2019 Qubit
 */

import { ofetch } from 'ofetch'
import FileMeta from './upload/FileMeta'
import FileProcessor from './upload/FileProcessor'

const MIN_CHUNK_SIZE = 262144

// By default axios will treat 308 as an error but it is used by GCS to indicate
// that the upload should continue
const validateStatus = (status) =>
  status >= 200 && (status < 300 || status === 308)

export class Upload {
  constructor(args, allowSmallChunks) {
    const opts = {
      chunkSize: MIN_CHUNK_SIZE,
      storage: window.localStorage,
      contentType: 'text/plain',
      onChunkUpload: () => {},
      id: null,
      url: null,
      file: null,
      ...args,
    }

    if (
      (opts.chunkSize % MIN_CHUNK_SIZE !== 0 || opts.chunkSize === 0) &&
      !allowSmallChunks
    ) {
      throw new Error('Upload error')
    }

    if (!opts.id || !opts.url || !opts.file) {
      throw new Error('Upload error')
    }

    this.opts = opts
    this.meta = new FileMeta(
      opts.id,
      opts.file.size,
      opts.chunkSize,
      opts.storage
    )
    this.processor = new FileProcessor(opts.file, opts.chunkSize)
    this.lastResult = null
  }

  async start() {
    const { meta, processor, opts, finished } = this

    const resumeUpload = async () => {
      const localResumeIndex = meta.getResumeIndex()
      const remoteResumeIndex = await getRemoteResumeIndex()

      const resumeIndex = Math.min(localResumeIndex, remoteResumeIndex)

      await processor.run(uploadChunk, resumeIndex)
    }

    const uploadChunk = async (index, chunk) => {
      const total = opts.file.size
      const start = index * opts.chunkSize
      const end = index * opts.chunkSize + chunk.byteLength - 1

      const headers = {
        'Content-Type': opts.contentType,
        'Content-Range': `bytes ${start}-${end}/${total}`,
      }

      const res = await safePut(opts.url, chunk, { headers, validateStatus })
      this.lastResult = res
      checkResponseStatus(res, opts, [200, 201, 308])

      opts.onChunkUpload({
        totalBytes: total,
        uploadedBytes: end + 1,
        chunkIndex: index,
        chunkLength: chunk.byteLength,
      })
    }

    const getRemoteResumeIndex = async () => {
      const headers = {
        'Content-Range': `bytes */${opts.file.size}`,
      }
      const res = await safePut(opts.url, null, { headers, validateStatus })

      checkResponseStatus(res, opts, [308])
      const header = res.headers.range
      const range = header.match(/(\d+?)-(\d+?)$/)
      const bytesReceived = parseInt(range[2]) + 1
      return Math.floor(bytesReceived / opts.chunkSize)
    }

    if (finished) {
      throw new Error('Upload error, finished')
    }

    if (meta.isResumable() && meta.getFileSize() === opts.file.size) {
      await resumeUpload()
    } else {
      await processor.run(uploadChunk)
    }
    meta.reset()
    this.finished = true
    return this.lastResult
  }

  pause() {
    this.processor.pause()
  }

  unpause() {
    this.processor.unpause()
  }

  cancel() {
    this.processor.pause()
    this.meta.reset()
  }
}

function checkResponseStatus(res, opts, allowed = []) {
  const { status } = res
  if (allowed.includes(status)) {
    return true
  }

  switch (status) {
    case 308:
      throw new Error('Upload error, resume incomplete')

    case 201:
    case 200:
      throw new Error('Upload error')

    case 404:
      throw new Error('Upload error')

    case 500:
    case 502:
    case 503:
    case 504:
      throw new Error('Upload error')

    default:
      throw new Error('Upload error')
  }
}

async function safePut(url, data, opts) {
  return await ofetch.raw(url, {
    method: 'PUT',
    body: data,
    ...opts
  })
}

export default defineNuxtPlugin(() => {
  return {
    provide: {
      upload: (options) => {
        return new Upload(options)
      }
    }
  }
})
